/**
 * useAdminPortalState — URL-state encapsulation for the `/admin` portal.
 *
 * The admin portal is a single page with four orthogonal pieces of state that
 * all live in the URL so deep links (and the back button) work:
 *
 *   - `tab`       — which top-level pill is active (organizations | people | tenants)
 *   - `filter`    — the active chip in the current tab's FilterChipRow
 *   - `drawer`    — `"<entity>:<uuid>"` if the right-side drawer is open
 *   - `orgFilter` — when set, scopes People/Tenants to a single org (cross-tab pivot)
 *
 * Why a hook over Zustand/Context: these are URL-bound by spec (deep-linkable,
 * survives refresh, back-button friendly). Wrapping `useSearchParams` lets the
 * page treat them as a typed bag while the underlying truth stays in the URL.
 *
 * Merge semantics: every setter merges into existing params and never clobbers
 * unrelated keys (e.g., `?theme=dark` set elsewhere survives a tab switch).
 * Passing `null` deletes the key. This is enforced by the pure `mergeParams`
 * helper, which is exported for unit tests.
 *
 * Tab fallback: if `tab` is absent or contains a value outside the AdminTab
 * union, we silently fall back to `'organizations'`. Garbage in the URL is a
 * UX problem, not a 500 — and the spec calls for graceful degradation.
 */

import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'

/* -------------------------------------------------------------------------- */
/* Public types                                                               */
/* -------------------------------------------------------------------------- */

export type AdminTab = 'organizations' | 'people' | 'tenants'

/**
 * Drawer identifier — `"<entity>:<uuid>"`. The hook is intentionally agnostic
 * about the entity vocabulary; downstream code parses on the colon. We use a
 * string alias (not a templated literal type) so callers can construct values
 * dynamically without TypeScript fighting them.
 */
export type DrawerEntity = string

export interface AdminPortalState {
  tab: AdminTab
  filter: string | null
  drawer: DrawerEntity | null
  orgFilter: string | null
}

export interface AdminPortalSetters {
  setTab(tab: AdminTab): void
  setFilter(filter: string | null): void
  setDrawer(entity: DrawerEntity | null): void
  setOrgFilter(orgId: string | null): void
  /**
   * Atomic multi-key update — used by cross-tab pivots like
   * "show me Tenants belonging to org X" where the caller needs to change
   * `tab`, `orgFilter`, and `filter` in a single URL transition. A single
   * navigation avoids two history entries and a flicker through the
   * intermediate state.
   */
  mergePivot(params: Partial<Pick<AdminPortalState, 'tab' | 'filter' | 'orgFilter'>>): void
}

/* -------------------------------------------------------------------------- */
/* Pure helpers — exported for unit tests                                     */
/* -------------------------------------------------------------------------- */

const VALID_TABS: ReadonlySet<string> = new Set(['organizations', 'people', 'tenants'])
const DEFAULT_TAB: AdminTab = 'organizations'

/** Coerce an arbitrary URL value into a valid `AdminTab`, defaulting on miss. */
export function parseTab(raw: string | null | undefined): AdminTab {
  return raw && VALID_TABS.has(raw) ? (raw as AdminTab) : DEFAULT_TAB
}

/**
 * Merge `changes` into a copy of `current`. `null` deletes the key; any string
 * (including '') sets it. Returns a *new* URLSearchParams so callers can hand
 * it to `setSearchParams` without sharing mutable state.
 *
 * Exported so the test suite can pin merge behavior without standing up a
 * Router or a renderer — same pattern as the pure helpers in
 * `useOrganizations.ts`.
 */
export function mergeParams(
  current: URLSearchParams,
  changes: Record<string, string | null>,
): URLSearchParams {
  const next = new URLSearchParams(current)
  for (const [key, value] of Object.entries(changes)) {
    if (value === null) {
      next.delete(key)
    } else {
      next.set(key, value)
    }
  }
  return next
}

/* -------------------------------------------------------------------------- */
/* Hook                                                                       */
/* -------------------------------------------------------------------------- */

export function useAdminPortalState(): AdminPortalState & AdminPortalSetters {
  const [searchParams, setSearchParams] = useSearchParams()

  // Read once per render and memoize. The `useSearchParams` instance is
  // reference-stable across renders only when the query string is unchanged,
  // so we depend on the serialized form to keep the memo predictable.
  const state = useMemo<AdminPortalState>(() => ({
    tab: parseTab(searchParams.get('tab')),
    filter: searchParams.get('filter'),
    drawer: searchParams.get('drawer'),
    orgFilter: searchParams.get('orgFilter'),
  }), [searchParams])

  // Single, generic apply path — every setter routes through `applyChanges`
  // so the merge-not-clobber invariant lives in exactly one place. The
  // functional-updater form of `setSearchParams` is the documented v6 API
  // for "merge into current"; see React Router useSearchParams docs.
  const applyChanges = useCallback((changes: Record<string, string | null>) => {
    setSearchParams(prev => mergeParams(prev, changes))
  }, [setSearchParams])

  const setTab = useCallback((tab: AdminTab) => {
    applyChanges({ tab })
  }, [applyChanges])

  const setFilter = useCallback((filter: string | null) => {
    applyChanges({ filter })
  }, [applyChanges])

  const setDrawer = useCallback((entity: DrawerEntity | null) => {
    applyChanges({ drawer: entity })
  }, [applyChanges])

  const setOrgFilter = useCallback((orgId: string | null) => {
    applyChanges({ orgFilter: orgId })
  }, [applyChanges])

  const mergePivot = useCallback(
    (params: Partial<Pick<AdminPortalState, 'tab' | 'filter' | 'orgFilter'>>) => {
      // Build the changes object explicitly so an absent key in `params`
      // becomes an absent key in the change set (i.e., "leave it alone"),
      // not a `null` (which would delete it).
      const changes: Record<string, string | null> = {}
      if ('tab' in params && params.tab !== undefined) changes.tab = params.tab
      if ('filter' in params) changes.filter = params.filter ?? null
      if ('orgFilter' in params) changes.orgFilter = params.orgFilter ?? null
      if (Object.keys(changes).length === 0) return
      applyChanges(changes)
    },
    [applyChanges],
  )

  return {
    ...state,
    setTab,
    setFilter,
    setDrawer,
    setOrgFilter,
    mergePivot,
  }
}
